import { SignUpIcon, SignUpTwoIcon } from '@authentic/loyalty-pwa/icons';
import { FunctionalComponent, h } from 'preact';
import { useContext, useState, useEffect } from 'preact/hooks';
import { UserProvider, UserServiceContext, useUser } from '@authentic/loyalty-pwa/providers';
import { SignUpContainer } from '../containers/SignUpContainer';
import { SignUpOne } from '../containers/SignUpOne';
import { SignUpTwo } from '../containers/SignUpTwo';
import { route } from '@authentic/loyalty-pwa/util';
import { SocialSignupProps, UserServiceInterface } from '@authentic/loyalty-pwa/interfaces';
import { UserData } from '@authentic/loyalty-pwa/model';

const fieldSteps: Record<string, number> = {
  firstName: 1,
  lastName: 1,
  email: 1,
  mobile: 1,
  birthdate: 1,
  password: 1,
  haveRefer: 2,
  referralCode: 2,
  receiveEmail: 2,
  receiveTexts: 2,
}

export const Registration: FunctionalComponent = () => {
  const user = useUser();
  const [customError, setCustomError] = useState({ });
  const [customErrorStepTwo, setCustomErrorStepTwo] = useState({});
  const [activeStep, setActiveStep] = useState(1);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [formValuesStep2, setFormValuesStep2] = useState<Record<string, string>>({});
  const userService = useContext(UserServiceContext);
  const hideFields: string[] = []
  const [hitOnBackButtonStep2, setHitOnBackButtonStep2] = useState(false);

  if (user?.appdata?.sso_fields) {
    // If we got mobile phone already, don't ask for it
    if (user?.appdata?.sso_fields?.mobile_phone)
      hideFields.push('mobile_phone')
    else
      fieldSteps.mobile = 2
    // If we got user email, hide it
    if (user?.appdata?.sso_fields?.email)
      hideFields.push('email')
    // First name and last name
    if (user?.appdata?.sso_fields?.last_name && user?.appdata?.sso_fields?.first_name)
      hideFields.push('name')
    else {
      fieldSteps.firstName = 2
      fieldSteps.lastName = 2
    }
  } else {
    hideFields.push('mobile_phone')
    hideFields.push('name')
  }
  console.log('Hiding fields', hideFields, 'based on', user?.appdata)

  useEffect(() => {
    if (user?.appdata?.sso_registration && user?.appdata?.sso_fields?.email) {
      setActiveStep(2)
    } else if (user?.appdata?.sso_registration) {
      console.log('Setting first name error')
      setCustomError({firstName: 'An unexpected error occurred. Please try again.'})
    }
  }, [user])

  const callbackFormOne = (data: any) => {
    setFormValues({
      email: data.email,
      password: data.password,
      passwordConfirmation: data.passwordConfirmation,
      firstName: data.firstName,
      lastName: data.lastName,
      mobile: data.mobile,
      birthdate: data.birthdate
    });
    setFormValuesStep2(prevValues => {
      return {
        ...prevValues,
        email: data.email
      };
    });
    setHitOnBackButtonStep2(false);
    setActiveStep(2);
    window.scrollTo(0, 0);
  };

  const callbackFormTwo = (data: any, onlyPersistData: boolean) => {
    setFormValuesStep2({
      email: data?.email,
      haveRefer: data?.haveRefer,
      receiveEmail: data?.receiveEmail,
      receiveTexts: data?.receiveTexts,
      referralCode: data?.referralCode,
      location: data?.location,
      locationLabelValue: data?.locationLabelValue,
      mobile: data?.mobile
    });

    if (onlyPersistData) {
      setActiveStep(1);
      return
    }

    if (formValues.email && data.email !== formValues.email) {
      setCustomErrorStepTwo({email: 'Email provided is different from step one.'})
      return
    }

    if (!user || !userService) {
      setCustomErrorStepTwo({email: 'Something went wrong. Please reload the page and try again.'})
      return
    }

    console.log('Signing up with', user?.appdata?.sso_registration ? 'SSO' : 'email')
    const signupResult = user?.appdata?.sso_registration ? signupWithSSO(userService, user, data) : signupWithEmail(userService, formValues, data)

    signupResult.then(result => {
      console.log('signup result', result)
      if (result.errorStepOne) {
        setCustomError(result.errorStepOne)
        setActiveStep(1)
      } else if (result.errorStepTwo) {
        setCustomErrorStepTwo(result.errorStepTwo)
        setActiveStep(2)
      } else if (result.success) {
        if (result.loggedin) {
          // User is already signed in with SSO
          route('/')
        } else {
          route('/email-verify')
        }
      }
    })

  };

  const stepCallback = (step: number) => {
    if (step === 1) {
      setHitOnBackButtonStep2(true);
    }
  }

  return (
    <div>
      {activeStep === 1 && (
        <SignUpContainer
          title="Create an Account"
          subtitle="Already have a Del Yeah! account? "
          activeStep={activeStep}
          headline="or sign up with an email address:"
          icon={<SignUpIcon />}
          stepCallbackOnClick={stepCallback}
        >
          <SignUpOne callbackSubmit={callbackFormOne} customError={customError} formValues={formValues} />
        </SignUpContainer>
      )}

      {(activeStep === 2) && (
        <SignUpContainer
          pageTitle="ALMOST DONE!"
          activeStep={activeStep}
          stepTitle="ALMOST DONE!"
          icon={<SignUpTwoIcon />}
          stepCallbackOnClick={stepCallback}
        >
          <SignUpTwo 
            callbackSubmit={callbackFormTwo} 
            customError={customErrorStepTwo} 
            formValues={formValuesStep2} 
            hideFields={hideFields}
            submitFormAction={hitOnBackButtonStep2} />
        </SignUpContainer>
      )}
    </div>
  );
};

type RegistrationResult = {
  success?: boolean
  errorStepOne?: Record<string, any>
  errorStepTwo?: Record<string, any>
  loggedin?: boolean
}

function signupWithSSO(userService: UserServiceInterface, user: UserData, data: Record<string, any>): Promise<RegistrationResult> {
  if (!user?.appdata?.sso?.token || !user.appdata.sso?.provider)
    return Promise.resolve({errorStepTwo: {email: 'Something went wrong, please try again later.'}})

  const registrationData: SocialSignupProps = {
    access_token: user.appdata.sso.token,
    provider: user.appdata.sso.provider,
    mode: 'sign_up',
    member: {
      haveRefer: data.haveRefer,
      receiveEmail: data.receiveEmail,
      receiveTexts: data.receiveTexts,
      referralCode: data.referralCode,
      favorite_location: data.location,
    }
  }

  // We set member up above, but Typescript doesn't notice that and insists memer could be missing
  if (!registrationData.member)
    return Promise.reject('This error is impossible')
  // Only include email and mobile phone if necessary
  if (!user?.appdata?.sso_fields?.mobile_phone)
    registrationData.member.mobile_phone = data.mobile
  if (!user?.appdata?.sso_fields?.first_name) {
    registrationData.member.first_name = data.firstName
    registrationData.member.last_name = data.lastName
  }

  console.log('Submitting SSO registration', registrationData)
  return userService.socialLogin(registrationData).then(response => {
    UserProvider.update({appdata: {
      ...UserProvider.get().appdata,
      sso_registration: false
    }})
    // Email is inconsistently available here, remove old email
    sessionStorage.setItem('email', '')

    if (!response.access_token) {
      return handleErrors(response, user, true);
    }
    return ({
      success: true,
      loggedin: true
    })
  })
}

// parse from MM/DD/YYYY to YYYY-MM-DD
const parseBirthDate = (birthdate: string) => {
  const dateItems = birthdate.split('/');
  return `${dateItems[2]}-${dateItems[0]}-${dateItems[1]}`;
};

function signupWithEmail(userService: UserServiceInterface, formValues: Record<string, any>, data: Record<string, any>): Promise<RegistrationResult> {
  let birthdate = {};
  if (formValues.birthdate) {
    birthdate = {
      birthdate: parseBirthDate(formValues.birthdate)
    }
  }
  return userService
  ?.signup({
    email: formValues.email,
    password: formValues.password,
    password_confirmation: formValues.passwordConfirmation,
    first_name: formValues.firstName,
    last_name: formValues.lastName,
    referral_code: data.referralCode,
    favorite_location: data.location,
    mobile_phone: formValues.mobile,
    receive_mail_offers: data.receiveEmail,
    receive_sms_offers: data.receiveTexts,
    ...birthdate
  })
  .then((res: any) => {
    if (res.success) {
      sessionStorage.setItem('email', formValues.email);
      return {success: true}
    } else {
      return handleErrors(res, null, false)
    }
  })
  .catch((res) => {
    console.error('signup.catch(): ', res)
    return {errorStepTwo: {
      email: 'Something went wrong, please try again later.'
    }}
  });
}

function handleErrors(res: any, user: UserData | null, sso: boolean) {
  // These errors are one-offs
  if (res.name === 'require_confirmation') {
    return {
      success: true,
      loggedin: false
    }
  }

  if (res['name'] === 'mobile_phone_taken' || res['code'] === 4413) {
    if (!sso)
      return {errorStepOne: {
        mobile: res['message']
      }}
    else {
      UserProvider.update({
        ...user,
        appdata: {
          ...user?.appdata,
          sso_fields: {
            ...user?.appdata?.sso_fields,
            mobile_phone: null
          }
        }
      })
      return {errorStepTwo: {
        mobile: res['message']
      }}
    }
  }

  if (res['name'] === 'invalid_record' && (res['code'] === 4063 || res['code'] === 4062)) {
    return {errorStepOne: {
      email: res['message']
    }};
  }

  if (res['name'] === 'not_available' && res['code'] === 4500) {
    return {errorStepTwo: {
      referralCode: res['message'],
      location: '',
      generic: ''
    }};
  }

  if (res['name'] === 'invalid_record' && res['code'] === 4400 && res['message'].indexOf('Password') > -1) {
    return {errorStepOne: {
      password: res['message']
    }};
  }

  if (res.name === 'invalid_grant' || res.name === 'missing_token') {
    return {errorStepOne: {
      generic: `Your login with ${user?.appdata?.sso?.provider} failed. Please try again.`
    }};
  }

  if (res.name === 'missing_required_fields') {
    const errorResult: any = {}
    Object.keys(res.errors).forEach(key => {
      const field = snakeToCamel(key);
      if (fieldSteps[field] === 1) {
        if (!errorResult.errorStepOne)
          errorResult.errorStepOne = {}
        errorResult.errorStepOne[field] = res.errors[key]
      } else if (fieldSteps[field] === 2) {
        if (!errorResult.errorStepTwo)
          errorResult.errorStepTwo = {}
        errorResult.errorStepTwo[field] = res.errors[key]
      } else {
        if (!errorResult.errorStepOne)
          errorResult.errorStepOne = {}
        errorResult.errorStepOne.generic = res.errors[key]  
      }
    })
    return errorResult
  }

  if (sso) {
    return {
      errorStepOne: {
        generic: `Unable to sign up with ${user?.appdata?.sso?.provider}.`
      }
    }
  }

  // If we made it this far, generic error
  return {
    errorStepOne: {
      referralCode: '',
      generic: res['message']
    }
  }
}

function snakeToCamel(str: string) {
  return str.replace(/([-_][a-z])/ig, letter => letter.toUpperCase().replace('-', '').replace('_', ''))
}